// src/components/modals/IssueCouponsModal.jsx
//
// Lets a school admin issue GEFEN coupons against one joined program's
// remaining budget. Primary flow: pick a coupon count + a price per
// coupon, issue, and get back that many codes — each rendered with a
// scannable QR code. Assigning specific coupons to named students by
// email is still supported, but tucked into an optional secondary
// section since it's not the main path anymore.
//
// Coupons are always shown grouped by "batch" (אצווה) — one batch per
// issueCoupons() call — rather than only a flat list, so the school
// can print everything from one issuance together and see, per batch,
// whether it was already printed (no more accidentally re-printing
// the same physical coupons). This grouping comes straight from the
// server (coupon.batchId / coupon.batchPrintedAt) and is always
// derived from the persisted coupon list — never from transient local
// state — so it looks the same whether the modal was just used to
// issue coupons or reopened later.
//
// QR codes are generated entirely client-side (no backend/image work
// needed — the QR just encodes the same coupon code the API already
// returns) via the `qrcode` npm package. Install it once:
//   npm install qrcode
//
// Visual language (shell, field styles, buttons) matches
// CreateLessonModal.jsx — rounded-3xl sheet, gradient header with an
// icon tile, InputField/SelectField with floating focus rings, and a
// sticky footer with a gradient primary button — so the two modals
// feel like the same product.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { useUser } from '../../contexts/UserContext';
import { useSchoolPrograms } from '../../contexts/SchoolProgramsContext';

/* ── Module-level icon constants ─────────────────────────────── */
const IconTicket = (
  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 9a2 2 0 100 4v3a2 2 0 002 2h12a2 2 0 002-2v-3a2 2 0 100-4V6a2 2 0 00-2-2H6a2 2 0 00-2 2v3z" />
    <path d="M9 4v16" strokeDasharray="2 2" />
  </svg>
);
const IconHash = (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M10 3L8.5 21h2L12 3h-2zm6 0l-1.5 18h2L18 3h-2zM4 8h16v2H4V8zm0 6h16v2H4v-2z" />
  </svg>
);
const IconMoney = (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm.5 15.5v1h-1v-1c-1.4-.1-2.6-.7-3.5-1.7l1-1c.7.8 1.6 1.2 2.7 1.3.9 0 2-.5 2-1.6 0-1.1-1.1-1.5-2.3-1.9-1.5-.5-3.1-1.1-3.1-3 0-1.4 1.1-2.5 2.7-2.7V6h1v.9c1.1.1 2 .6 2.7 1.4l-1 1c-.5-.6-1.2-1-2.2-1-.8 0-1.7.4-1.7 1.3 0 .9.9 1.2 2.2 1.6 1.6.5 3.3 1.2 3.3 3.3 0 1.6-1.2 2.7-2.8 3z" />
  </svg>
);
const IconUsers = (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
  </svg>
);
const IconPrint = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
    <path d="M6 14h12v8H6z" />
  </svg>
);

/* ── Shared field helpers (same pattern as CreateLessonModal) ──── */
const FieldLabel = ({ htmlFor, required, focused, hasValue, children }) => (
  <label
    htmlFor={htmlFor}
    className={`block text-xs font-bold uppercase tracking-wide mb-1.5 text-right transition-colors duration-150
                ${focused || hasValue ? 'text-indigo-500' : 'text-gray-500'}`}
  >
    {children}{' '}
    {required && <span className="text-red-400 normal-case font-normal">*</span>}
  </label>
);

const FieldError = ({ msg }) =>
  msg ? (
    <p className="mt-1.5 text-xs text-red-500 font-medium flex items-center justify-end gap-1">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {msg}
    </p>
  ) : null;

const InputField = ({
  label,
  name,
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  required = false,
  icon,
  dir = 'rtl',
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  return (
    <div className="min-w-0">
      <FieldLabel htmlFor={name} required={required} focused={focused} hasValue={!!value}>
        {label}
      </FieldLabel>
      <div className="relative flex items-center">
        {icon && (
          <span className={`absolute right-3 pointer-events-none transition-colors duration-150 ${focused ? 'text-indigo-400' : 'text-gray-400'}`}>
            {icon}
          </span>
        )}
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          dir={dir}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          className={`block w-full min-w-0 h-10 ${icon ? 'pr-9' : 'pr-3'} pl-3 text-base sm:text-sm rounded-xl border bg-white text-gray-800 text-right
                      outline-none transition-all duration-150 appearance-none
                      ${error ? 'border-red-300 bg-red-50 ring-2 ring-red-100' : focused ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-gray-200 hover:border-indigo-300'}`}
          {...props}
        />
      </div>
      <FieldError msg={error} />
    </div>
  );
};

const TextAreaField = ({ label, name, value, onChange, placeholder, hint, rows = 4 }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div className="min-w-0">
      <FieldLabel htmlFor={name} focused={focused} hasValue={!!value}>
        {label}
      </FieldLabel>
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        rows={rows}
        placeholder={placeholder}
        className={`block w-full rounded-xl border bg-white text-gray-800 text-right text-sm px-3 py-2
                    outline-none transition-all duration-150
                    ${focused ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-gray-200 hover:border-indigo-300'}`}
      />
      {hint && <p className="mt-1 text-[11px] text-gray-400">{hint}</p>}
    </div>
  );
};

/* ── Domain helpers ──────────────────────────────────────────── */
const formatMoney = (v) => `₪${Number(v || 0).toLocaleString('he-IL', { maximumFractionDigits: 2 })}`;

const formatDateTime = (v) => {
  if (!v) return '';
  const d = new Date(String(v).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const STATUS_LABELS = {
  ISSUED: 'ממתין למימוש',
  REDEEMED: 'מומש',
  CANCELLED: 'בוטל',
};

const STATUS_BADGE_CLASS = {
  ISSUED: 'bg-amber-50 text-amber-700',
  REDEEMED: 'bg-emerald-50 text-emerald-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

// Renders a coupon code as a QR image, generated on the fly and
// cached per-code (module-level, shared across every instance of this
// component and with the print-window builder below) so re-renders
// and re-prints never regenerate an image that's already been made.
const qrCache = new Map();

const getQrDataUrl = async (code, size = 140) => {
  if (qrCache.has(code)) return qrCache.get(code);
  const dataUrl = await QRCode.toDataURL(code, { width: size, margin: 1 });
  qrCache.set(code, dataUrl);
  return dataUrl;
};

const CouponQr = ({ code, size = 96 }) => {
  const [src, setSrc] = useState(qrCache.get(code) || null);

  useEffect(() => {
    let cancelled = false;
    if (qrCache.has(code)) {
      setSrc(qrCache.get(code));
      return undefined;
    }
    getQrDataUrl(code, size)
      .then((dataUrl) => {
        if (!cancelled) setSrc(dataUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [code, size]);

  if (!src) {
    return (
      <div className="flex items-center justify-center bg-gray-50 rounded-md text-[10px] text-gray-400" style={{ width: size, height: size }}>
        טוען QR…
      </div>
    );
  }
  return <img src={src} alt={`QR של קופון ${code}`} width={size} height={size} className="rounded-md border border-gray-100" />;
};

// Groups the flat coupon list into batches (newest first). Coupons
// issued before batching existed have batchId === null — those all
// land together under one synthetic "legacy" bucket at the end, since
// there's no way to tell which issuance they came from.
const groupIntoBatches = (coupons) => {
  const map = new Map();
  for (const c of coupons) {
    const key = c.batchId ?? 'legacy';
    if (!map.has(key)) {
      map.set(key, {
        batchId: c.batchId, // null for the legacy bucket
        coupons: [],
        valueAmount: c.valueAmount,
        createdAt: c.createdAt,
        printedAt: c.batchPrintedAt || null,
      });
    }
    const batch = map.get(key);
    batch.coupons.push(c);
    if (c.createdAt && (!batch.createdAt || c.createdAt < batch.createdAt)) {
      batch.createdAt = c.createdAt;
    }
  }
  const batches = Array.from(map.values());
  batches.sort((a, b) => {
    if (a.batchId === null) return 1; // legacy bucket always last
    if (b.batchId === null) return -1;
    return (b.batchId || 0) - (a.batchId || 0); // newest batch first
  });
  return batches;
};

// Builds a full, self-contained HTML document for printing one
// batch's coupons: one wide row per coupon (code, value, QR, who it's
// assigned to if anyone), with a dashed line — plus a scissors mark —
// between each pair, so the sheet can be cut into individual coupons
// after printing. QR images are embedded as data URLs (already
// generated before this is called), so the print window needs no
// network access at all.
const buildBatchPrintHtml = (program, batch, qrByCode) => {
  const rows = batch.coupons
    .map((c) => {
      const qr = qrByCode.get(c.code) || '';
      const assignedLine = c.assignedEmail
        ? `<div class="assigned">שויך ל: ${c.assignedEmail}</div>`
        : `<div class="assigned open">קופון פתוח</div>`;
      return `
        <div class="coupon">
          <div class="qr">${qr ? `<img src="${qr}" width="120" height="120" alt="QR" />` : ''}</div>
          <div class="info">
            <div class="program">${program.programName || ''}</div>
            <div class="code">${c.code}</div>
            <div class="value">${formatMoney(c.valueAmount)}</div>
            ${assignedLine}
          </div>
        </div>
        <div class="cut-line"><span>✂ קו חיתוך</span></div>
      `;
    })
    .join('\n');

  return `<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
<meta charset="UTF-8" />
<title>קופונים — ${program.programName || ''}</title>
<style>
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; direction: rtl; margin: 0; color: #111; }
  h1 { font-size: 14px; margin: 0 0 10mm 0; }
  .coupon {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    min-height: 34mm;
    padding: 6mm 8mm;
  }
  .coupon .info { display: flex; flex-direction: column; gap: 3px; flex: 1; }
  .coupon .program { font-size: 11px; color: #666; }
  .coupon .code { font-family: 'Courier New', monospace; font-size: 22px; font-weight: bold; letter-spacing: 1px; }
  .coupon .value { font-size: 16px; color: #222; }
  .coupon .assigned { font-size: 11px; color: #4f46e5; }
  .coupon .assigned.open { color: #999; }
  .coupon .qr { flex-shrink: 0; }
  .cut-line {
    display: flex;
    align-items: center;
    justify-content: center;
    border-top: 2px dashed #aaa;
    position: relative;
    height: 0;
  }
  .cut-line span {
    position: relative;
    top: -8px;
    background: #fff;
    padding: 0 6px;
    font-size: 9px;
    color: #aaa;
    letter-spacing: 1px;
  }
  .coupon:not(:last-child), .cut-line { page-break-inside: avoid; }
  @media print {
    .cut-line span { color: #999; }
  }
</style>
</head>
<body>
  <h1>${program.programName || ''} — אצווה ${batch.batchId ?? ''} · ${batch.coupons.length} קופונים · ${formatDateTime(batch.createdAt)}</h1>
  ${rows}
</body>
</html>`;
};

/* ── Main component ──────────────────────────────────────────── */
const IssueCouponsModal = ({ program, onClose }) => {
  const { showAlert } = useUser();
  const { issueCoupons, fetchCoupons, markCouponBatchPrinted } = useSchoolPrograms();

  // Primary flow: how many coupons, and how much each is worth.
  const [couponCount, setCouponCount] = useState('');
  const [valueAmount, setValueAmount] = useState('');

  // Secondary/optional flow: assign specific coupons to named
  // students by email, in addition to the plain count above.
  const [showStudentAssignment, setShowStudentAssignment] = useState(false);
  const [studentsText, setStudentsText] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [printingBatchId, setPrintingBatchId] = useState(null);

  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [openQrRow, setOpenQrRow] = useState(null);
  const [expandedBatchId, setExpandedBatchId] = useState(null);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const close = () => onCloseRef.current?.();

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const loadCoupons = async () => {
    setCouponsLoading(true);
    try {
      const items = await fetchCoupons(program.id);
      setCoupons(items);
    } catch (err) {
      showAlert?.(err.message || 'שגיאה בטעינת רשימת הקופונים', 'warning');
    } finally {
      setCouponsLoading(false);
    }
  };

  useEffect(() => {
    loadCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program.id]);

  // Coupons are always shown grouped by batch — issuing new ones just
  // means the list is reloaded, so the newest batch appears at the
  // top like any other; there's no separate "just issued" state that
  // could go stale or disappear on reopen.
  const batches = useMemo(() => groupIntoBatches(coupons), [coupons]);

  useEffect(() => {
    // Auto-expand the newest batch right after it's created, so the
    // school sees what they just issued without an extra click.
    if (batches.length > 0 && expandedBatchId === null) {
      setExpandedBatchId(batches[0].batchId ?? 'legacy');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches.length]);

  const remainingBudget = Number(program.remainingBudget || 0);

  const parsedEmails = useMemo(
    () =>
      studentsText
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean),
    [studentsText]
  );

  const parsedCouponCount = Math.max(0, parseInt(couponCount, 10) || 0);
  const totalCoupons = parsedCouponCount + (showStudentAssignment ? parsedEmails.length : 0);
  const totalCost = totalCoupons * (Number(valueAmount) || 0);
  const overBudget = totalCost > remainingBudget + 0.00001;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const value = Number(valueAmount);
    if (!value || value <= 0) {
      showAlert?.('יש להזין מחיר לקופון גדול מאפס', 'warning');
      return;
    }
    if (totalCoupons <= 0) {
      showAlert?.('יש להזין מספר קופונים גדול מאפס (או לשייך תלמידים בסעיף האופציונלי)', 'warning');
      return;
    }
    if (overBudget) {
      showAlert?.(
        `התקציב הנותר (${formatMoney(remainingBudget)}) אינו מספיק להנפקת קופונים בסך ${formatMoney(totalCost)}`,
        'warning'
      );
      return;
    }

    setSubmitting(true);
    try {
      const result = await issueCoupons(program.id, {
        value_amount: value,
        open_count: parsedCouponCount,
        students: showStudentAssignment ? parsedEmails : [],
      });
      setCouponCount('');
      setValueAmount('');
      setStudentsText('');
      showAlert?.('הקופונים הונפקו בהצלחה', 'success');
      setExpandedBatchId(result?.batchId ?? null);
      await loadCoupons();
    } catch (err) {
      showAlert?.(err.message || 'הנפקת הקופונים נכשלה', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Opens a print-ready window with every coupon in this batch laid
  // out as a wide card with a dashed cut-line between each one, then
  // triggers the browser's print dialog — which itself offers "save
  // as PDF", so this covers both printing on paper and getting a
  // file. Marks the batch as printed server-side right after, so the
  // list shows it was handled and the school doesn't reprint by
  // mistake.
  const handlePrintBatch = async (batch) => {
    const batchKey = batch.batchId ?? 'legacy';
    setPrintingBatchId(batchKey);
    try {
      await Promise.all(batch.coupons.map((c) => getQrDataUrl(c.code, 140)));
      const qrByCode = new Map(batch.coupons.map((c) => [c.code, qrCache.get(c.code)]));

      const printWindow = window.open('', '_blank', 'width=900,height=700');
      if (!printWindow) {
        showAlert?.('החלון הקופץ נחסם — יש לאפשר חלונות קופצים כדי להדפיס', 'warning');
        return;
      }
      const html = buildBatchPrintHtml(program, batch, qrByCode);
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      // Let the images paint before the print dialog opens.
      setTimeout(() => {
        try { printWindow.print(); } catch { /* ignore */ }
      }, 300);

      if (batch.batchId != null && !batch.printedAt) {
        try {
          await markCouponBatchPrinted(program.id, batch.batchId);
          await loadCoupons();
        } catch (err) {
          // Non-fatal — the print dialog already opened either way.
          showAlert?.(err.message || 'הדפסה בוצעה, אך סימון האצווה כמודפסת נכשל', 'warning');
        }
      }
    } finally {
      setPrintingBatchId(null);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="issue-coupons-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]" onClick={close} />

      <div
        className="relative z-[1] w-full sm:max-w-2xl bg-white
                   rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20
                   flex flex-col max-h-[96dvh] sm:max-h-[90vh] overflow-hidden"
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-7 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              {IconTicket}
            </div>
            <div className="min-w-0">
              <h2 id="issue-coupons-title" className="text-gray-800 font-bold text-base sm:text-lg leading-tight">
                הנפקת קופונים
              </h2>
              <p className="text-gray-500 text-xs mt-0.5 truncate">
                {program.programName ? `תוכנית: ${program.programName}` : 'הנפקת קופוני גפ״ן חדשים'}
                {' · '}
                תקציב פנוי: <span className="font-semibold text-indigo-600">{formatMoney(remainingBudget)}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="סגור"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form
          id="issue-coupons-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-7 py-5"
        >
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InputField
                label="מספר קופונים"
                name="couponCount"
                type="number"
                min="0"
                step="1"
                dir="ltr"
                value={couponCount}
                onChange={(e) => setCouponCount(e.target.value)}
                placeholder="לדוגמה: 20"
                icon={IconHash}
              />
              <InputField
                label="מחיר לקופון (₪)"
                name="valueAmount"
                type="number"
                min="0"
                step="0.01"
                dir="ltr"
                value={valueAmount}
                onChange={(e) => setValueAmount(e.target.value)}
                placeholder="לדוגמה: 150"
                required
                icon={IconMoney}
              />
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowStudentAssignment((v) => !v)}
                className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-semibold"
              >
                <span className="w-5 h-5 rounded-lg bg-indigo-50 flex items-center justify-center">{IconUsers}</span>
                {showStudentAssignment ? 'הסתרת שיוך לתלמידים ספציפיים' : 'שיוך חלק מהקופונים לתלמידים ספציפיים (אופציונלי)'}
              </button>

              {showStudentAssignment && (
                <div className="mt-3">
                  <TextAreaField
                    label="אימייל תלמיד בכל שורה"
                    name="studentsText"
                    value={studentsText}
                    onChange={(e) => setStudentsText(e.target.value)}
                    placeholder={'student1@example.com\nstudent2@example.com'}
                    hint={
                      parsedEmails.length > 0
                        ? `${parsedEmails.length} תלמידים זוהו — יונפק קופון ייעודי לכל אחד, בנוסף לכמות שלמעלה`
                        : 'יונפק קופון ייעודי לכל תלמיד, בנוסף לכמות שלמעלה'
                    }
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between text-xs bg-gray-50 rounded-2xl px-4 py-3 border border-gray-100">
              <span className="text-gray-500 font-medium">{totalCoupons} קופונים · עלות כוללת</span>
              <span className={`font-bold text-sm ${overBudget ? 'text-red-600' : 'text-gray-800'}`}>{formatMoney(totalCost)}</span>
            </div>

            {/* Coupon batches — always visible, from the persisted list */}
            <div className="pt-1">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2.5 text-right">
                אצוות קופונים ({coupons.length} קופונים בסה״כ)
              </p>

              {couponsLoading ? (
                <p className="text-xs text-gray-400">טוען…</p>
              ) : batches.length === 0 ? (
                <p className="text-xs text-gray-400">טרם הונפקו קופונים לתוכנית זו.</p>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {batches.map((batch) => {
                    const batchKey = batch.batchId ?? 'legacy';
                    const isExpanded = expandedBatchId === batchKey;
                    const isPrinting = printingBatchId === batchKey;
                    return (
                      <div
                        key={batchKey}
                        className={`rounded-2xl border-2 overflow-hidden transition-colors duration-150 ${
                          isExpanded ? 'border-indigo-200' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex flex-wrap items-center gap-2 px-3.5 py-3 bg-white">
                          <button
                            type="button"
                            onClick={() => setExpandedBatchId(isExpanded ? null : batchKey)}
                            className="flex-1 min-w-[10rem] text-right flex items-center gap-2"
                          >
                            <svg
                              className={`w-3.5 h-3.5 flex-shrink-0 text-gray-400 transition-transform duration-150 ${isExpanded ? '-rotate-90' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                            <span className="text-sm font-bold text-gray-800">
                              {batch.batchId != null ? `אצווה #${batch.batchId}` : 'קופונים ישנים (ללא אצווה)'}
                            </span>
                            <span className="text-[11px] text-gray-400 font-normal truncate">
                              {batch.coupons.length} קופונים · {formatMoney(batch.valueAmount)} ליחידה
                              {batch.createdAt ? ` · ${formatDateTime(batch.createdAt)}` : ''}
                            </span>
                          </button>

                          {batch.printedAt ? (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                              ✓ הודפס · {formatDateTime(batch.printedAt)}
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                              טרם הודפס
                            </span>
                          )}

                          <button
                            type="button"
                            onClick={() => handlePrintBatch(batch)}
                            disabled={isPrinting}
                            className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 hover:text-indigo-800
                                       bg-indigo-50 hover:bg-indigo-100 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-50"
                          >
                            {IconPrint}
                            {isPrinting ? 'מכין…' : 'הדפסת אצווה'}
                          </button>
                        </div>

                        {isExpanded && (
                          <div className="overflow-x-auto p-2 border-t border-gray-100 bg-gray-50/60">
                            <table className="w-full text-xs text-right">
                              <thead>
                                <tr className="text-gray-400 border-b border-gray-200">
                                  <th className="py-1.5 px-1.5 font-semibold">קוד</th>
                                  <th className="py-1.5 px-1.5 font-semibold">ערך</th>
                                  <th className="py-1.5 px-1.5 font-semibold">תלמיד</th>
                                  <th className="py-1.5 px-1.5 font-semibold">סטטוס</th>
                                  <th className="py-1.5 px-1.5 font-semibold">QR</th>
                                </tr>
                              </thead>
                              <tbody>
                                {batch.coupons.map((c) => (
                                  <React.Fragment key={c.id}>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-1.5 px-1.5 font-mono">{c.code}</td>
                                      <td className="py-1.5 px-1.5">{formatMoney(c.valueAmount)}</td>
                                      <td className="py-1.5 px-1.5">{c.studentEmail || c.studentName || 'פתוח'}</td>
                                      <td className="py-1.5 px-1.5">
                                        <span className={`px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE_CLASS[c.status] || 'bg-gray-100 text-gray-500'}`}>
                                          {STATUS_LABELS[c.status] || c.status}
                                        </span>
                                      </td>
                                      <td className="py-1.5 px-1.5">
                                        <button
                                          type="button"
                                          onClick={() => setOpenQrRow((prev) => (prev === c.id ? null : c.id))}
                                          className="text-indigo-600 hover:text-indigo-800 text-[11px] underline font-medium"
                                        >
                                          {openQrRow === c.id ? 'הסתרה' : 'הצגה'}
                                        </button>
                                      </td>
                                    </tr>
                                    {openQrRow === c.id && (
                                      <tr className="border-b border-gray-100">
                                        <td colSpan={5} className="py-2 px-1.5">
                                          <CouponQr code={c.code} size={100} />
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={close}
            className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500
                       hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
          >
            סגירה
          </button>
          <button
            type="submit"
            form="issue-coupons-form"
            disabled={submitting}
            className="h-10 px-6 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold
                       shadow-md shadow-indigo-200 hover:shadow-lg hover:shadow-indigo-300 hover:-translate-y-px
                       disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0
                       transition-all duration-150 flex items-center gap-2"
          >
            {submitting && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {!submitting && (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
              </svg>
            )}
            הנפקת קופונים
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default IssueCouponsModal;